// look for [data-autocomplete]. currently only works for input elements (meant for text inputs).
//  the value of data-autocomplete is the type of autocomplete.
//  autocomplete types determine the API URL to fetch data from (e.g. keyword, location, and occupation)
// another valid data attribute is [data-autocomplete-length]
//   this sets the minimum length of the element value before kicking off the API call.
//   without this value, we use the default length of 2 characters.
// NOTE: This component does not automatically bind elements. It depends on a promise in the session.
// TODO: Should we update this component to be shared with other apps? It's specific to Seeker now because of URLs and autocomplete types.
import { getRandomId, debounce, isEmpty } from '../utilities';
import { AutocompleteListState, AutocompleteResult, ACClickEventDetails } from '../interfaces';
import { getUsajConfig } from '../session/sessionmain';

const ValidAutocompleteTypes = [
    'keyword', 'location', 'occupation', 'language', 'nonsearchlocation'
];
const SearchLinkTypes = ['keyword', 'location'];
const MinLength = 2;
let DataHome = '';
let BaseUrl = '';
let autocompleteLists: AutocompleteListState[] = [];

function autocompleteBlur(elem: HTMLInputElement, id: string) {    
    setTimeout(() => {
        if (document.activeElement?.id.startsWith('autocomplete') || document.activeElement === elem) {
            return;
        }

        setAutocompleteDisplay(elem, id, false);
    }, 500);
}

function autocompleteFocus(elem: HTMLInputElement, id: string) {
    const acElem = document.querySelector<HTMLUListElement>(`#autocomplete${id}`);

    if (acElem?.children !== undefined && acElem?.children.length > 0) {
        elem.setAttribute('aria-expanded', 'true');
        document.querySelector<HTMLElement>(`#autocomplete${id}`)?.classList.remove('hidden');
    }
}

function autocompleteInput(elem: HTMLInputElement, id: string) {
    const acType = elem.getAttribute('data-autocomplete')?.toLowerCase();

    if (acType === undefined || ValidAutocompleteTypes.indexOf(acType) < 0) {
        return;
    }
    if (elem.value.length < Number.parseInt(elem.getAttribute('data-autocomplete-length') || MinLength.toString())) {
        populateAutocompleteList(elem, null, id, acType);
        return;
    }
    displayLoadingIndicator(elem, id);
    fetch(`${DataHome}api/Autocomplete/${acType}?term=${encodeURIComponent(elem.value)}`, {
        method: 'GET',
        mode: 'cors',
        cache: 'force-cache',
        headers: { "Content-Type": "application/json" }
    })
        .then(response => {
            if (!response.ok) {
                throw new Error(`Response returned ${response.status}`);
            }
            return response.json();
        })
        .then(data => {
            populateAutocompleteList(elem, data, id, acType);
        })
        .catch(err => console.debug('Error', err))
        .finally(() => displayLoadingIndicator(elem, id, false));
}

function autocompleteKeyup(evt: KeyboardEvent, elem: HTMLInputElement, id: string) {
    const theKey = evt.code;

    if (document.querySelector<HTMLElement>(`#autocomplete${id}`) === null) {
        return;
    }
    if (theKey === 'ArrowUp' || theKey === 'ArrowDown') {
        evt.preventDefault();
        handleArrowKeypress(elem, id, theKey);
    }
    else if (theKey === 'Enter' || theKey === 'Space') {
        evt.preventDefault();
        if (document.activeElement === elem || !document.activeElement?.id.startsWith('autocomplete')) { return; }
        const activeElement = (<HTMLElement>document.activeElement);

        if (activeElement.getAttribute('href') !== null) {
            location.href = activeElement.getAttribute('href') || "#";
        }
        setAutocompleteDisplay(elem, id, false);
    }
    else if (theKey === 'Escape') {
        evt.preventDefault();
        elem.focus();
        setAutocompleteDisplay(elem, id, false);
    }
}

function handleArrowKeypress(elem: HTMLElement, id: string, theKey: string) {
    const activeId = document.activeElement?.id;
    const listState = autocompleteLists.find(i => i.id === id);
    if (listState === undefined) { return; }

    if (document.activeElement === elem) {
        document.querySelector<HTMLElement>(`#autocompleteItem${id}-1`)?.focus();
        return;
    }

    let currentElemId = parseInt(activeId?.substring(activeId.indexOf('-')+1) || "0");
    let nextElemId = 0;
    if (theKey === 'ArrowUp') {
        nextElemId = (currentElemId < 2) ? listState.length : currentElemId - 1;
    }
    if (theKey === 'ArrowDown') {
        nextElemId = (currentElemId >= listState.length) ? 1 : currentElemId + 1;
    }
    const selectedElem = document.querySelector<HTMLElement>(`#autocompleteItem${id}-${nextElemId}`);
    selectedElem?.focus();
    selectedElem?.setAttribute('aria-selected', 'true');
    if (currentElemId > 0) {
        document.querySelector<HTMLElement>(`#autocompleteItem${id}-${currentElemId}`)?.setAttribute('aria-selected', 'false');
    }
}

function displayLoadingIndicator(elem: HTMLInputElement, id: string, isDisplayed: boolean = true) {
    let indicator = document.querySelector<HTMLDivElement>(`#indicator${id}`);

    if (indicator === null && isDisplayed) {
        indicator = document.createElement('div');
        const indicatorImage = document.createElement('img');

        indicator.id = `indicator${id}`;
        indicator.className = "absolute p-0 m-0";
        indicatorImage.src = '/img/icons/loader.gif';
        indicatorImage.alt = 'Loading. Please wait.';
        indicatorImage.className = "w-6 h-6";
        indicator.appendChild(indicatorImage);
        document.body.appendChild(indicator);
    }
    if (isDisplayed && indicator !== null) {
        indicator.classList.remove('hidden');
        indicator.style.top = (elem.getBoundingClientRect().top + 10).toString() + "px";
        indicator.style.left = (elem.getBoundingClientRect().right - 30).toString() + "px";
    }
    else if (indicator !== null) {
        setTimeout(() => indicator?.classList.add('hidden'), 250);
    }
}

function buildAutocompleteUI(elem: HTMLInputElement, id: string) {
    const autocompleteId = "autocomplete" + id;
    const autocomplete = document.createElement('ul');
    const assistiveElement = document.createElement('div');

    elem.setAttribute('autocomplete', 'off');
    elem.setAttribute('role', 'combobox');
    elem.setAttribute('aria-expanded', 'false');
    elem.setAttribute('aria-autocomplete', 'list');
    elem.setAttribute('aria-controls', autocompleteId);

    autocomplete.id = autocompleteId;
    autocomplete.tabIndex = 0;
    autocomplete.className = "absolute bg-white shadow-lg shadow-primary-dark p-0 m-0 border border-primary-dark z-9000 hidden";
    autocomplete.style.width = (elem.getBoundingClientRect().width).toString() + "px";
    autocomplete.setAttribute('role', 'listbox');

    assistiveElement.id = "assistive" + autocompleteId;
    assistiveElement.className = "sr-only";
    assistiveElement.setAttribute('role', 'status');
    assistiveElement.setAttribute('aria-live', 'polite');
    assistiveElement.setAttribute('aria-atomic', 'true');
    assistiveElement.innerText = "When autocomplete results are available use up and down arrows to review and space or enter to select.  Touch device users, explore by touch or with swipe gestures.";

    elem.after(autocomplete);
    autocomplete.after(assistiveElement);

    autocomplete.addEventListener('click', () => {
        elem.focus();
        autocomplete?.classList.remove('hidden');
    });

    addEventListener('resize', () => resizeAutocomplete(elem, autocomplete));
}

function resizeAutocomplete(elem: HTMLInputElement, acElem: HTMLElement) {
    acElem.style.width = (elem.getBoundingClientRect().width).toString() + "px";
}

function populateAutocompleteList(elem: HTMLInputElement, data: any, id: string, acType: string = '') {
    const acElem = document.querySelector<HTMLElement>(`#autocomplete${id}`);

    if (acElem === null) { return; }
    acElem.innerHTML = "";

    if (data === null) { return; }

    resizeAutocomplete(elem, acElem);
    acElem.tabIndex = -1;

    const acItemCSS = "inline-block no-underline w-full py-2 px-4 text-black cursor-pointer hover:pl-5 hover:bg-primary-lighter focus:bg-primary-lighter";
    let currentPosition = 0;
    if (acType === "location" && elem.value.toLowerCase() === "remote") {
        data["remote jobs"] = [
            {
                Code: "false",
                Name: "Exclude remote jobs"
            },
            {
                Code: "true",
                Name: "Only show remote jobs"
            }
        ]
    }
    for (const property in data) {
        var isRemoteJobs = property.toLowerCase() === "remote jobs";
        const results = (<AutocompleteResult[]>data[property]);

        if (results?.length > 0) {
            const catListItem = document.createElement('li');
            catListItem.innerText = property.toUpperCase();
            catListItem.className = "bg-gray-lightest py-2 px-4";
            if (!isRemoteJobs) {
                acElem.appendChild(catListItem);
            }

            results.forEach(result => {
                const listItem = document.createElement('li');
                ++currentPosition;
                listItem.tabIndex = -1;

                if (SearchLinkTypes.indexOf(acType) > -1 && location.href.toLowerCase().indexOf('search/results') === -1) {
                    const searchLink = getKeywordLink(result, acType, property.toLowerCase());
                    searchLink.title = result.Name;
                    searchLink.setAttribute('role', 'option');
                    searchLink.setAttribute('aria-posinset', (currentPosition).toString());
                    searchLink.setAttribute('aria-selected', 'false');
                    searchLink.innerHTML = getFormattedACName(searchLink.innerHTML, elem.value);
                    searchLink.className = acItemCSS + " visited:text-black hover:text-black";
                    searchLink.id = `autocompleteItem${id}-${currentPosition}`;
                    searchLink.tabIndex = -1;
                    listItem.appendChild(searchLink);
                    searchLink.addEventListener('focusin', () => elem.value = result.Name);
                    searchLink.addEventListener('blur', () => autocompleteBlur(elem, id));
                    searchLink.addEventListener('keydown', evt => autocompleteKeyup(evt, elem, id));
                }
                else {
                    listItem.title = result.Name;
                    listItem.setAttribute('aria-selected', 'false');
                    listItem.setAttribute('role', 'option');
                    listItem.setAttribute('aria-posinset', (currentPosition).toString());
                    listItem.className = acItemCSS;
                    listItem.innerHTML = getFormattedACName(result.Name, elem.value);
                    listItem.id = `autocompleteItem${id}-${currentPosition}`;
                    listItem.addEventListener('focusin', () => elem.value = result.Name);
                    listItem.addEventListener('blur', () => autocompleteBlur(elem, id));
                    listItem.addEventListener('keydown', evt => autocompleteKeyup(evt, elem, id));
                }

                if (!isRemoteJobs) {
                    acElem.appendChild(listItem);
                } else {
                    acElem.prepend(listItem);
                }

                const detail: ACClickEventDetails = {
                    Type: acType,
                    Name: result.Name,
                    Code: result.Code,
                    ParamProperty: getParamProperty(result, acType, property.toLowerCase())
                }

                listItem.addEventListener('click', () => {
                    elem.value = result.Name;
                    populateAutocompleteList(elem, null, id, acType);
                    document.dispatchEvent(new CustomEvent("acclick", { detail: detail }));
                    const acElem = document.querySelector<HTMLInputElement>(`[aria-controls=autocomplete${id}]`);
                    if (!acElem) {
                        return;
                    }
                    acElem.value = '';
                });
                listItem.addEventListener('keydown', (evt) => {
                    const theKey = evt.code;
                    if (theKey === 'Enter' || theKey === 'Space') {
                        document.dispatchEvent(new CustomEvent("acclick", { detail: detail }));
                        const acElem = document.querySelector<HTMLInputElement>(`[aria-controls=autocomplete${id}]`);
                        if (!acElem) {
                            return;
                        }
                        acElem.value = '';
                    }
                });
            });

            if (isRemoteJobs) {
                acElem.prepend(catListItem);
            }
        }
    }

    if (currentPosition > 0) {
        setAutocompleteDisplay(elem, id, true);
        const acListIndex = autocompleteLists.findIndex(i => i.id === id);
        if (acListIndex > -1) {
            autocompleteLists[acListIndex].length = currentPosition;
        } else {
            autocompleteLists.push({ id: id, length: currentPosition });
        }

        acElem.childNodes.forEach(node => {
            const self = (<HTMLElement>node);
            if (self.getAttribute('aria-posinset') !== null) {
                self.setAttribute('aria-setsize', currentPosition.toString())
            }
        });
    }
}

function setAutocompleteDisplay(elem: HTMLInputElement, id: string, isOpen: boolean) {
    const acElem = document.querySelector<HTMLDivElement>(`#autocomplete${id}`);
    if (acElem === null) { return; }

    if (isOpen) {
        elem.setAttribute('aria-expanded', 'true');
        acElem.classList.remove('hidden');
    }
    else {
        elem.setAttribute('aria-expanded', 'false');
        acElem.classList.add('hidden');
    }
}

function getSearchUrl(parameter: string) {
    let searchUrl = BaseUrl + "/Search/Results?" + parameter;
    return searchUrl;
}

function getParamProperty(data: AutocompleteResult, acType: string = '', category: string = ''): string {
    if (isEmpty(category) || SearchLinkTypes.indexOf(acType) === -1) {
        return '';
    }
    if (acType === 'location') {
        return category === 'remote jobs' ? 'RemoteIndicator' : 'LocationName';
    }
    switch (category) {
        case 'series':
            return !isEmpty(data.ParentName) ? 'JobCategoryCode' : 'JobCategoryFamily';
        case 'agencies':
            return 'Agency';
        case 'departments':
            return 'Department';
        case 'occupations':
            return 'SocTitle';
        case 'job titles':
            return 'JobTitle';
        default:
            return 'Keyword';
    }
}

function getSearchParameter(data: AutocompleteResult, acType: string = '', category: string = ''): string {
    const code = encodeURIComponent(data.Code);
    if (acType.toLowerCase() === 'location') { return category === 'remote jobs' ? `rmi=${code}` : `l=${code}`; }
    if (category == 'series') { return (data.ParentName !== null && data.ParentName !== '') ? `j=${code}` : `jf=${code}`; }
    if (category === 'agencies') { return `a=${code}`; }
    if (category === 'departments') { return `d=${code}`; }
    if (category === 'occupations') { return `soc=${code}`; }
    if (category === 'job titles') { return `jt=${code}`; }
    if (category === 'remote jobs') { return `rmi=${code}`; }
    return `k=${code}`;
}

function getKeywordLink(data: AutocompleteResult, acType: string = '', category: string = ''): HTMLAnchorElement {
    const link = document.createElement('a');

    link.innerHTML = (category === 'series') ? `${data.Code} - ${data.Name}` : data.Name;
    link.href = getSearchUrl(getSearchParameter(data, acType, category));
    return link;
}

function getFormattedACName(acName: string, acTerm: string) {
    let term = acTerm.trim().toLowerCase();
    let termIndex = acName.toLowerCase().indexOf(term.trim().toLowerCase());

    if (termIndex < 0) { return acName; }
    return `${acName.substr(0, termIndex)}<strong>${acName.substr(termIndex, term.length)}</strong>${acName.substr(termIndex + term.length) }`;
}

export function bindAutocompletes() {
    let config = getUsajConfig();
    DataHome = config.DataHome;
    BaseUrl = config.BaseUrl;

    document.querySelectorAll<HTMLInputElement>("[data-autocomplete]").forEach(elem => {
        const acEvent = elem.getAttribute('autocomplete-event');
        const id = getRandomId();

        buildAutocompleteUI(elem, id);
        if (acEvent === null || acEvent !== 'true') {
            elem.setAttribute('autocomplete-event', 'true');
            elem.addEventListener('input', debounce(() => autocompleteInput(elem, id), 300));
            elem.addEventListener('keyup', evt => autocompleteKeyup(evt, elem, id));
            elem.addEventListener('blur', () => autocompleteBlur(elem, id));
            elem.addEventListener('focus', () => autocompleteFocus(elem, id));
        }
    });
}
